import { useEffect } from "react";
import { CheckCircle, XCircle, AlertTriangle, X } from "lucide-react";
import { cn } from "@/lib/utils/cn";

export type ToastVariant = "success" | "error" | "warning";

export interface ToastItem {
  id: string;
  message: string;
  variant: ToastVariant;
}

interface ToastProps {
  toast: ToastItem;
  onDismiss: (id: string) => void;
}

const CONFIG: Record<
  ToastVariant,
  { icon: React.ElementType; bg: string; text: string; border: string }
> = {
  success: {
    icon: CheckCircle,
    bg: "bg-metro-green-950",
    text: "text-white",
    border: "border-metro-green-800",
  },
  error: {
    icon: XCircle,
    bg: "bg-red-600",
    text: "text-white",
    border: "border-red-500",
  },
  warning: {
    icon: AlertTriangle,
    bg: "bg-amber-500",
    text: "text-white",
    border: "border-amber-400",
  },
};

// ─── Single toast ─────────────────────────────────────────────────────────────

export function Toast({ toast, onDismiss }: ToastProps) {
  const cfg = CONFIG[toast.variant];
  const Icon = cfg.icon;

  useEffect(() => {
    const t = setTimeout(() => onDismiss(toast.id), 4000);
    return () => clearTimeout(t);
  }, [toast.id, onDismiss]);

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-xl border px-4 py-3 shadow-modal",
        "animate-[fadeUp_0.25s_ease-out_forwards]",
        cfg.bg,
        cfg.text,
        cfg.border,
      )}
      role="alert"
    >
      <Icon size={16} className="shrink-0" />
      <p className="flex-1 text-sm font-medium">{toast.message}</p>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        className="ml-2 opacity-70 transition hover:opacity-100"
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}

// ─── Toast container ──────────────────────────────────────────────────────────

interface ToastContainerProps {
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
}

export function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  if (!toasts.length) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[100] flex flex-col gap-2 w-80">
      {toasts.map((t) => (
        <Toast key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}